/**
 * CDID Activity, cycle-shaped (TPF, 2026-09-16) — replaces the per-session
 * `RunHistory` table on this tab. The unit here is the CYCLE (one ingress
 * firing / scheduled orchestrator / hot-lane admission, its workers, and the
 * lander waves that landed its tickets), not the GitHub Actions run: see the
 * `CdidCycle` doc comment in `packages/types/src/autobuild.ts` for why a
 * session-shaped table under-reported both duration and resolution.
 */
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { CdidCycle, CdidCycleTrigger } from '@a7y/types';
import { api } from '@/lib/api';
import {
  fmtDuration,
  fmtHm,
  fmtLocal,
  LIFECYCLE_STATE_LABELS,
  LIFECYCLE_STATE_STYLES,
  relativeAgo,
  useNow,
} from './shared';
import { CopyButton } from '@/components/CopyButton';
import { OutcomeBadge } from './OutcomeBadge';
import { RunnerBadge } from './RunnerBadge';
import { CdidHealthStrip } from './CdidHealthStrip';

const NEUTRAL_STAGE_STYLE = 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400';

const TRIGGER_META: Record<CdidCycleTrigger, { label: string; className: string }> = {
  schedule: { label: 'Scheduled', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300' },
  ingress: { label: 'Ingress', className: 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300' },
  'hot-lane': { label: 'Hot lane', className: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' },
  manual: { label: 'Manual', className: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300' },
  // A recorded gap, not "we know it was nothing" — styled to say so.
  unknown: { label: 'Unknown', className: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400' },
};

const STATE_META: Record<CdidCycle['state'], { label: string; className: string }> = {
  'in-flight': { label: 'In flight', className: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300' },
  settled: { label: 'Settled', className: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400' },
  stuck: { label: 'Stuck', className: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300' },
};

function TriggerChip({ trigger }: { trigger: CdidCycleTrigger }) {
  const meta = TRIGGER_META[trigger];
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

/**
 * "128k out · 6.5M cached" — a glance-scale figure, deliberately not
 * `Intl.NumberFormat('compact')`: that formatter's case (`128K` for en-US,
 * `128k`/`6.5m` for en-GB, lowercase million) is locale-dependent and neither
 * locale produces the mixed lower-k/upper-M pairing an operator scanning
 * cost figures elsewhere in this app already reads.
 */
function fmtTokenCount(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (n >= 1_000) return `${Math.round(n / 1000)}k`;
  return `${n}`;
}

function TokenSummary({ tokens }: { tokens: CdidCycle['tokens'] }) {
  if (tokens == null) {
    return (
      <span className="text-zinc-400" title="no ledger row">
        —
      </span>
    );
  }
  // "cached" is the cache-adjacent volume (tokens read from cache plus
  // tokens written to it), kept apart from `output` — conflating the two
  // would hide that most of a worker's token volume is usually cache reads,
  // not model output.
  const cached = tokens.cacheRead + tokens.cacheCreation;
  return (
    <span title={`${tokens.output} output, ${tokens.cacheRead} cache read, ${tokens.cacheCreation} cache write, ${tokens.turns} turns`}>
      {fmtTokenCount(tokens.output)} out · {fmtTokenCount(cached)} cached
    </span>
  );
}

/** One ticket chip, coloured by its current CDID lifecycle stage. */
function TicketChip({ ticket }: { ticket: CdidCycle['tickets'][number] }) {
  const style = ticket.stage != null ? LIFECYCLE_STATE_STYLES[ticket.stage] : NEUTRAL_STAGE_STYLE;
  const stageLabel = ticket.stage != null ? LIFECYCLE_STATE_LABELS[ticket.stage] : 'No stage recorded';
  const title = [`${ticket.title ?? ticket.id}`, stageLabel, ticket.reason].filter(Boolean).join(' — ');
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${style}`}
      title={title}
    >
      {ticket.id}
    </span>
  );
}

function CopyCycleLink({ cycle }: { cycle: CdidCycle }) {
  return (
    <CopyButton
      value={`${window.location.origin}/admin/automation/cdid/${cycle.stamp}`}
      label={`link to the CDID cycle started ${fmtLocal(cycle.startedAt)}`}
    />
  );
}

function WorkersDisclosure({ workers }: { workers: CdidCycle['workers'] }) {
  if (workers.length === 0) {
    return <p className="text-[11px] text-zinc-400">No workers dispatched.</p>;
  }
  return (
    <ul className="space-y-1">
      {workers.map((w) => (
        <li key={w.stamp} className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <Link
            to={`/admin/automation/cdid/${w.stamp}`}
            className="font-mono text-zinc-700 hover:underline dark:text-zinc-300"
          >
            {w.stamp}
          </Link>
          {w.attempt != null && <span className="text-zinc-400">attempt {w.attempt}</span>}
          <OutcomeBadge outcome={w.outcome} />
          <RunnerBadge runnerHost={w.runnerHost} />
          <span className="text-zinc-500 dark:text-zinc-400">
            {w.durationSeconds != null ? fmtDuration(w.durationSeconds) : '—'}
          </span>
          {w.verdict != null && (
            <span className="font-semibold text-zinc-800 dark:text-zinc-200">
              {w.verdict}
              {w.reason != null && <span className="font-normal text-zinc-500"> — {w.reason}</span>}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function TicketsDisclosure({ tickets }: { tickets: CdidCycle['tickets'] }) {
  if (tickets.length === 0) {
    return <p className="text-[11px] text-zinc-400">No tickets touched.</p>;
  }
  return (
    <ul className="space-y-1">
      {tickets.map((t) => {
        const style = t.stage != null ? LIFECYCLE_STATE_STYLES[t.stage] : NEUTRAL_STAGE_STYLE;
        const stageLabel = t.stage != null ? LIFECYCLE_STATE_LABELS[t.stage] : 'No stage';
        return (
          <li key={t.id} className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="font-mono text-zinc-700 dark:text-zinc-300">{t.id}</span>
            {t.title && <span className="text-zinc-500 dark:text-zinc-400">{t.title}</span>}
            <span className={`whitespace-nowrap rounded-full px-1.5 py-0.5 font-medium ${style}`}>
              {stageLabel}
            </span>
            <span className="text-zinc-400">{t.attempts} attempt{t.attempts === 1 ? '' : 's'}</span>
            {t.landedBy != null && (
              <a
                href={`https://github.com/ApptonomyInc/a7y/actions/runs/${t.landedBy}`}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                landed by wave {t.landedBy}
              </a>
            )}
            {t.landedAt != null && (
              <span className="text-zinc-400">{fmtLocal(t.landedAt)}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function CycleRow({
  cycle,
  isOpen,
  onToggle,
}: {
  cycle: CdidCycle;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const stateMeta = STATE_META[cycle.state];
  // Rework signal: more worker attempts than tickets means at least one
  // ticket needed more than one pass.
  const rework = cycle.attempts > cycle.tickets.length;
  return (
    <>
      <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-900 align-top">
        <td className="px-1 py-2">
          <CopyCycleLink cycle={cycle} />
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1 whitespace-nowrap">
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={`${isOpen ? 'Hide' : 'Show'} workers and tickets for the cycle started ${fmtLocal(cycle.startedAt)}`}
              onClick={onToggle}
              className="rounded px-1 text-[11px] text-zinc-500 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 dark:text-zinc-400 dark:hover:bg-zinc-800"
            >
              <span aria-hidden className="inline-block w-2">
                {isOpen ? '▾' : '▸'}
              </span>
            </button>
            <Link
              to={`/admin/automation/cdid/${cycle.stamp}`}
              title={cycle.stamp}
              className="tabular-nums text-xs text-zinc-800 hover:underline dark:text-zinc-200"
            >
              {fmtLocal(cycle.startedAt)}
            </Link>
          </div>
        </td>
        <td className="px-3 py-2">
          <TriggerChip trigger={cycle.trigger} />
        </td>
        <td className="px-3 py-2">
          <RunnerBadge runnerHost={cycle.runnerHost} runnerName={cycle.runnerName} />
        </td>
        <td className="px-3 py-2">
          {cycle.tickets.length === 0 ? (
            <span className="text-zinc-400">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {cycle.tickets.map((t) => (
                <TicketChip key={t.id} ticket={t} />
              ))}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-xs tabular-nums">
          {cycle.resolvedCount > 0 ? (
            <span className="text-emerald-600 dark:text-emerald-400">
              {cycle.resolvedCount} / {cycle.tickets.length}
            </span>
          ) : (
            <span className="text-zinc-400">
              {cycle.resolvedCount} / {cycle.tickets.length}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-xs tabular-nums">
          <span
            className={rework ? 'text-red-600 dark:text-red-400' : 'text-zinc-700 dark:text-zinc-300'}
            title={rework ? 'More worker attempts than tickets — at least one ticket needed rework' : undefined}
          >
            {cycle.attempts}
          </span>
        </td>
        <td className="px-3 py-2 text-xs tabular-nums">
          {fmtDuration(cycle.durationSeconds)}
          {cycle.state === 'in-flight' ? ' (elapsed)' : ''}
        </td>
        <td className="px-3 py-2 text-xs">
          <div className="text-zinc-700 dark:text-zinc-300">{fmtHm(cycle.workerSeconds)}</div>
          <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
            <TokenSummary tokens={cycle.tokens} />
          </div>
        </td>
        <td className="px-3 py-2">
          <span
            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${stateMeta.className}`}
          >
            {stateMeta.label}
          </span>
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-zinc-50/60 dark:bg-zinc-900/60">
          <td />
          <td colSpan={9} className="px-3 py-2">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-zinc-400">
                  Workers
                </div>
                <WorkersDisclosure workers={cycle.workers} />
              </div>
              <div>
                <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-zinc-400">
                  Tickets
                </div>
                <TicketsDisclosure tickets={cycle.tickets} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function CdidCyclesTab() {
  const now = useNow();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const query = useQuery({
    queryKey: ['cdid-cycles'],
    queryFn: () => api.getCdidCycles(),
    refetchInterval: 60_000,
  });

  if (query.isLoading) {
    return <div className="p-6 text-sm text-zinc-500">Loading CDID cycles…</div>;
  }
  if (query.isError) {
    return (
      <div className="p-4 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 text-sm text-red-700 dark:text-red-300">
        CDID cycles failed to load. Check the Worker logs for /admin/autobuild/cdid/cycles.
      </div>
    );
  }

  if (!query.data) {
    return <div className="p-6 text-sm text-zinc-500">Loading CDID cycles…</div>;
  }
  const { health, cycles } = query.data;

  const toggle = (stamp: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(stamp)) next.add(stamp);
      return next;
    });

  return (
    <div className="space-y-4">
      <CdidHealthStrip health={health} now={now} />

      {cycles.length === 0 ? (
        <div className="p-6 text-sm text-zinc-500">
          No material CDID cycles in the window — nothing dispatched a worker.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
          <table className="min-w-[72rem] w-full table-fixed text-sm">
            <colgroup>
              <col className="w-[2.25rem]" />
              <col className="w-[11rem]" />
              <col className="w-[6rem]" />
              <col className="w-[6.5rem]" />
              <col className="w-[16rem]" />
              <col className="w-[5.5rem]" />
              <col className="w-[5.5rem]" />
              <col className="w-[6rem]" />
              <col className="w-[8rem]" />
              <col className="w-[6rem]" />
            </colgroup>
            <thead className="bg-zinc-50 dark:bg-zinc-900 text-left text-xs text-zinc-500">
              <tr>
                <th className="px-1 py-2 font-medium">
                  <span className="sr-only">Copy link to cycle</span>
                </th>
                <th className="px-3 py-2 font-medium">Started</th>
                <th className="px-3 py-2 font-medium">Trigger</th>
                <th className="px-3 py-2 font-medium">Runner</th>
                <th className="px-3 py-2 font-medium">Tickets</th>
                <th className="px-3 py-2 font-medium">Resolved</th>
                <th className="px-3 py-2 font-medium">Attempts</th>
                <th className="px-3 py-2 font-medium">Duration</th>
                <th className="px-3 py-2 font-medium">Spend</th>
                <th className="px-3 py-2 font-medium">State</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800 bg-white dark:bg-zinc-950">
              {cycles.map((cycle) => (
                <Fragment key={cycle.stamp}>
                  <CycleRow
                    cycle={cycle}
                    isOpen={expanded.has(cycle.stamp)}
                    onToggle={() => toggle(cycle.stamp)}
                  />
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-zinc-400">
        Ingress fired {health.ingress24h.fired}× in 24h and armed {health.ingress24h.armed}; hot
        lane fired {health.hotLane24h.fired}× and armed {health.hotLane24h.armed}; lander last ran{' '}
        {relativeAgo(health.landerLastRanAt, now)}. Idle firings and lander waves are not rows.
      </p>
    </div>
  );
}
